import { useState, useEffect } from 'react';

/**
 * Chaves do localStorage para preferências da Sidebar
 */
const STORAGE_KEYS = {
  COLLAPSED: 'app_sidebar_collapsed',
  EXPANDED_MENUS: 'app_sidebar_expanded_menus',
} as const;

/**
 * Interface de preferências da Sidebar
 */
export interface SidebarPreferences {
  isCollapsed: boolean;
  expandedMenus: string[];
}

/**
 * Hook customizado para gerenciar preferências da Sidebar com persistência em localStorage
 *
 * Salva e recupera automaticamente:
 * - Estado da sidebar (colapsada/expandida)
 * - Menus expandidos (array de IDs)
 *
 * @example
 * ```tsx
 * const {
 *   isCollapsed,
 *   setIsCollapsed,
 *   expandedMenus,
 *   setExpandedMenus,
 *   toggleMenu
 * } = useSidebarPreferences();
 *
 * // Verificar se menu está aberto
 * const isMenuOpen = expandedMenus.includes('settings');
 *
 * // Toggle menu
 * <button onClick={() => toggleMenu('settings')}>
 * ```
 */
export const useSidebarPreferences = () => {
  // Carregar preferências do localStorage na inicialização
  const [isCollapsed, setIsCollapsedState] = useState<boolean>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEYS.COLLAPSED);
      // Default: sidebar expandida (false)
      return stored ? JSON.parse(stored) : false;
    } catch {
      return false;
    }
  });

  const [expandedMenus, setExpandedMenusState] = useState<string[]>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEYS.EXPANDED_MENUS);
      // Default: nenhum menu expandido
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });

  // Salvar isCollapsed no localStorage sempre que mudar
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEYS.COLLAPSED, JSON.stringify(isCollapsed));
    } catch (error) {
      console.error('Erro ao salvar isCollapsed no localStorage:', error);
    }
  }, [isCollapsed]);

  // Salvar expandedMenus no localStorage sempre que mudar
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEYS.EXPANDED_MENUS, JSON.stringify(expandedMenus));
    } catch (error) {
      console.error('Erro ao salvar expandedMenus no localStorage:', error);
    }
  }, [expandedMenus]);

  /**
   * Helper function para toggle de menu específico
   * @param menuId - ID do menu a ser expandido/colapsado
   */
  const toggleMenu = (menuId: string) => {
    setExpandedMenusState(prev =>
      prev.includes(menuId)
        ? prev.filter(id => id !== menuId)
        : [...prev, menuId]
    );
  };

  return {
    // Estado da sidebar
    isCollapsed,
    setIsCollapsed: setIsCollapsedState,

    // Menus expandidos
    expandedMenus,
    setExpandedMenus: setExpandedMenusState,

    // Helper function
    toggleMenu,
  };
};
